import {
  ContentAccessUser,
  ContentWorkflow,
  WorkflowState,
  isRejectedPublisherReview,
} from '@amzn/awscat-pattern-library-service-model';

import { ActiveRole } from '../services/UserService';
import { isLockedByOtherUser } from './LoadedDocument';

// Draft stages an author may edit: Draft (NEW/UNMODIFIED/MODIFIED), Quality
// check, or a rejected Publisher review (so the author can address feedback).
const AUTHOR_EDITABLE_STATES = new Set([
  WorkflowState.NEW,
  WorkflowState.UNMODIFIED,
  WorkflowState.MODIFIED,
  WorkflowState.QUALITY_CHECK,
]);

/** Why the current user may not edit the draft. */
export type EditIneligibilityReason = 'role' | 'notOwner' | 'lockedByOther' | 'workflowState';

/** Inputs needed to decide whether the current user may edit a draft. */
export interface EditEligibilityInput {
  readonly workflow: ContentWorkflow | undefined;
  readonly userId: string;
  readonly activeRole: ActiveRole;
  readonly coAuthors: readonly ContentAccessUser[];
}

/** Whether the current user may edit the draft, and if not, why. */
export type EditEligibility =
  | { readonly canEdit: true }
  | { readonly canEdit: false; readonly reason: EditIneligibilityReason };

/** Whether the draft is in a stage the author is allowed to edit. */
function isAuthorEditableState(workflow: ContentWorkflow): boolean {
  if (workflow.draftState && AUTHOR_EDITABLE_STATES.has(workflow.draftState)) return true;
  return isRejectedPublisherReview(workflow);
}

/**
 * Decides whether the current user may edit the loaded draft, and if not,
 * why. A lock held by another user blocks editing; a system lock (e.g. an
 * in-progress quality-check scan) does not. Reason precedence: role,
 * notOwner, lockedByOther, workflowState.
 */
export function evaluateEditEligibility(input: EditEligibilityInput): EditEligibility {
  const { workflow, userId, activeRole, coAuthors } = input;

  // Bar raisers never edit; this mirrors PublicationStatePanel's default branch.
  if (activeRole === 'bar_raiser') return { canEdit: false, reason: 'role' };

  // Without a workflow there is nothing to edit yet.
  if (!workflow) return { canEdit: false, reason: 'workflowState' };

  // Admin is not subject to the author ownership/lock gate — only barred during
  // a publisher review that has not been rejected.
  if (activeRole === 'admin') {
    const isBlockedByReview =
      workflow.draftState === WorkflowState.PUBLISHER_REVIEW &&
      !isRejectedPublisherReview(workflow);
    return isBlockedByReview ? { canEdit: false, reason: 'workflowState' } : { canEdit: true };
  }

  // Author flow: must own the content (or be a co-author).
  const isOwner = userId === workflow.ownerId || coAuthors.some((c) => c.userId === userId);
  if (!isOwner) return { canEdit: false, reason: 'notOwner' };

  // A lock held by another (non-system) user blocks editing.
  if (isLockedByOtherUser(workflow, userId)) return { canEdit: false, reason: 'lockedByOther' };

  // Finally, the draft must be in an author-editable stage.
  if (!isAuthorEditableState(workflow)) return { canEdit: false, reason: 'workflowState' };

  return { canEdit: true };
}
